import axios from 'axios';

const STANDARD = 'rxnorm';
const TOKEN_STORAGE_KEY = 'ms_platform_token';
const OFFICIAL_HOST = 'rxnorm.qunxue.tw';
const PRODUCTION_API_URL = 'https://api-med-standard.qunxue.tw/api';
const MOHW_API_URL = 'https://backend.twstd.mohw.gov.tw/api';

export const LOINC_SITE_URL = 'https://loinc.qunxue.tw';
export const RXNORM_SITE_URL = 'https://rxnorm.qunxue.tw';
export const SNOMEDCT_SITE_URL = 'https://snomedct.qunxue.tw';

const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
const isLocalhost = currentHost === 'localhost';
export const IS_OFFICIAL_HOST = currentHost === OFFICIAL_HOST;
const isMohwHost = currentHost.endsWith('.mohw.gov.tw');

const envBaseUrl = (import.meta.env.VITE_API_BASE_URL || '').trim();
const defaultApiUrl = envBaseUrl
  ? envBaseUrl.replace(/\/$/, '')
  : import.meta.env.DEV
    ? 'http://localhost:8000/api'
    : '/api';
export const API_URL = isLocalhost
  ? defaultApiUrl
  : isMohwHost
    ? MOHW_API_URL
    : IS_OFFICIAL_HOST
      ? PRODUCTION_API_URL
      : defaultApiUrl;

export const API_BASE_URL = API_URL.replace(/\/api\/?$/, '');

const isTestingEnvironment = currentHost.endsWith('.qunxue.tw');

export const PROJECT_WEBSITE_LINKS = isMohwHost
  ? {
    medStandard: 'https://medstandard.mohw.gov.tw/',
    thds: 'https://thds.mohw.gov.tw/',
    thrs: 'https://thrs.mohw.gov.tw/',
    thas: 'https://thas.mohw.gov.tw/',
    snomedCt: 'https://twsnomedct.mohw.gov.tw/',
    loinc: 'https://twloinc.mohw.gov.tw/',
    rxnorm: 'https://twrxnorm.mohw.gov.tw/',
    smartHealth: 'https://smarthealth.mohw.gov.tw/',
    aiCenter: 'https://aicenter.mohw.gov.tw/AC/mp-208.html',
    aiCenter1: 'http://aicenter1.mohw.gov.tw/',
    aiCenter2: 'http://aicenter2.mohw.gov.tw/',
  }
  : isTestingEnvironment
    ? {
      medStandard: 'https://medstandard.mohw.gov.tw/',
      thds: 'https://thds.mohw.gov.tw/',
      thrs: 'https://thrs.mohw.gov.tw/',
      thas: 'https://thas.mohw.gov.tw/',
      snomedCt: 'https://snomedct.qunxue.tw/',
      loinc: 'https://loinc.qunxue.tw/',
      rxnorm: 'https://rxnorm.qunxue.tw/',
      smartHealth: 'https://smarthealth.mohw.gov.tw/',
      aiCenter: 'https://aicenter.mohw.gov.tw/AC/mp-208.html',
      aiCenter1: 'http://aicenter1.mohw.gov.tw/',
      aiCenter2: 'http://aicenter2.mohw.gov.tw/',
    }
    : {
      medStandard: 'https://medstandard.mohw.gov.tw/',
      thds: 'https://thds.mohw.gov.tw/',
      thrs: 'https://thrs.mohw.gov.tw/',
      thas: 'https://thas.mohw.gov.tw/',
      snomedCt: 'http://localhost:5178/',
      loinc: 'http://localhost:5176/',
      rxnorm: 'http://localhost:5177/',
      smartHealth: 'https://smarthealth.mohw.gov.tw/',
      aiCenter: 'https://aicenter.mohw.gov.tw/AC/mp-208.html',
      aiCenter1: 'http://aicenter1.mohw.gov.tw/',
      aiCenter2: 'http://aicenter2.mohw.gov.tw/',
    };

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

function getAuthToken() {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

function setAuthToken(token) {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

function clearAuthToken() {
  localStorage.removeItem(TOKEN_STORAGE_KEY);
  localStorage.removeItem('user_data');
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem('user_data');
  }
}

apiClient.interceptors.request.use(
  (config) => {
    config.headers['X-Standard'] = STANDARD;
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

apiClient.interceptors.response.use(
  (response) => {
    const data = response?.data;
    if (
      data &&
      typeof data === 'object' &&
      Object.prototype.hasOwnProperty.call(data, 'status') &&
      Object.prototype.hasOwnProperty.call(data, 'message') &&
      Object.prototype.hasOwnProperty.call(data, 'content')
    ) {
      response.data = data.content;
    }
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';

    if (status === 401 && (url.includes('/auth/me') || url.includes('/me'))) {
      clearAuthToken();
    }

    // 2026-08-06：攔截 MOBILE_BIND_REQUIRED，導向 /bind-mobile
    // 排除：/auth/me 自己回傳這個狀態時不應跳轉；/auth/mobile/* 本身就在綁定流程
    if (
      status === 403
      && error?.response?.data?.code === 'MOBILE_BIND_REQUIRED'
      && !url.includes('/auth/me')
      && !url.includes('/auth/mobile/')
    ) {
      const redirect = error.response.data.redirect || '/bind-mobile';
      const currentPath = window.location.pathname;
      // 避免重複導向
      if (currentPath !== redirect) {
        window.location.href = redirect;
        return Promise.reject(new Error('MOBILE_BIND_REQUIRED'));
      }
    }

    const data = error?.response?.data;
    if (
      data &&
      typeof data === 'object' &&
      Object.prototype.hasOwnProperty.call(data, 'status') &&
      Object.prototype.hasOwnProperty.call(data, 'message') &&
      Object.prototype.hasOwnProperty.call(data, 'content')
    ) {
      if (data.content && typeof data.content === 'object' && !Array.isArray(data.content)) {
        error.response.data = {
          ...data.content,
          status: data.status,
          message: data.message,
          content: data.content,
        };
      } else {
        error.response.data = {
          status: data.status,
          message: data.message,
          content: data.content,
        };
      }
    }
    return Promise.reject(error);
  },
);

export const authAPI = {
  async register(payload) {
    // 2026-08-06 改動：註冊改為簡訊流程，不會回傳 token；token 於 verifyRegisterCode 後才取得
    const res = await apiClient.post('/auth/register', payload);
    return res;
  },

  checkEmail(payload) {
    return apiClient.post('/auth/check-email', payload);
  },

  async login(payload) {
    const res = await apiClient.post('/auth/login', { ...payload, standard: payload?.standard || 'rxnorm' });
    const token = res?.data?.token;
    if (token) setAuthToken(token);
    return res;
  },

  forgotPassword(payload) {
    return apiClient.post('/auth/forgot-password', { ...payload, standard: STANDARD });
  },

  resetPassword(payload) {
    return apiClient.post('/auth/reset-password', payload);
  },

  changePassword(payload) {
    return apiClient.post('/auth/change-password', payload);
  },

  resendEmailVerification(payload) {
    return apiClient.post('/auth/email/verification-notification', payload);
  },

  resendMobileCode(payload) {
    return apiClient.post('/auth/mobile/resend-code', payload);
  },

  verifyMobileCode(payload) {
    return apiClient.post('/auth/mobile/verify-code', payload);
  },

  // 簡訊認證流程（2026-08-06 新增）
  async sendRegisterSms(payload) {
    return apiClient.post('/auth/mobile/send-register-code', { ...payload, standard: STANDARD });
  },

  async verifyRegisterCode(payload) {
    const res = await apiClient.post('/auth/mobile/verify-register-code', { ...payload, standard: STANDARD });
    const token = res?.data?.token;
    if (token) setAuthToken(token);
    return res;
  },

  async sendResetSms(payload) {
    return apiClient.post('/auth/mobile/send-reset-code', { ...payload, standard: STANDARD });
  },

  async resetPasswordBySms(payload) {
    return apiClient.post('/auth/mobile/reset-password', { ...payload, standard: STANDARD });
  },

  async verifyEmail(payload) {
    const res = await apiClient.post('/auth/email/verify', payload);
    const token = res?.data?.token;
    if (token) setAuthToken(token);
    return res;
  },

  async logout() {
    const res = await apiClient.post('/auth/logout');
    clearAuthToken();
    return res;
  },

  refresh() {
    return apiClient.post('/auth/refresh');
  },

  async me() {
    try {
      return await apiClient.get('/auth/me', { params: { standard: STANDARD } });
    } catch (error) {
      if (error?.response?.status === 401) {
        clearAuthToken();
      }
      throw error;
    }
  },

  regenerateApiKey() {
    return apiClient.post('/auth/regenerate-api-key');
  },

  updateProfile(payload) {
    return apiClient.put('/auth/profile', payload);
  },

  agreeAdditionalTerms(payload = {}) {
    return apiClient.post('/auth/additional-terms/agree', { ...payload, standard: payload?.standard || STANDARD });
  },

  getAdditionalTermsStatus() {
    return apiClient.get('/auth/additional-terms/status', { params: { standard: STANDARD } });
  },
};

export const contentAPI = {
  getAdditionalTerms() {
    return apiClient.get(`/${STANDARD}/additional-terms`);
  },

  platformIntro(params = {}) {
    return apiClient.get(`/${STANDARD}/platform-intro`, { params });
  },

  platformIntroSection(section) {
    return apiClient.get(`/${STANDARD}/platform-intro/${section}`);
  },

  expertDirectory() {
    return apiClient.get(`/${STANDARD}/expert-directory`);
  },

  standardIntro(params = {}) {
    return apiClient.get(`/${STANDARD}/standard-intro`, { params });
  },

  workshops() {
    return apiClient.get(`/${STANDARD}/workshops`);
  },

  workshopMaterials() {
    return apiClient.get(`/${STANDARD}/workshop-materials`);
  },

  downloadWorkshopMaterial(id) {
    return apiClient.get(`/${STANDARD}/workshop-materials/${id}/download`, {
      responseType: 'blob',
    });
  },

  announcements(params = {}) {
    return apiClient.get(`/${STANDARD}/announcements`, { params });
  },

  getApplicationProfile() {
    return apiClient.get(`/${STANDARD}/member/applications/profile`);
  },

  checkApplicationOrganization(organization) {
    return apiClient.post(`/${STANDARD}/member/applications/check-organization`, { organization });
  },

  submitApplication(formData) {
    return apiClient.post(`/${STANDARD}/member/applications`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  getApplicationStatus() {
    return apiClient.get(`/${STANDARD}/member/applications/status`);
  },

  datasets(params = {}) {
    return apiClient.get(`/${STANDARD}/datasets`, { params });
  },

  projectWebsites() {
    return apiClient.get(`/${STANDARD}/project-websites`);
  },
};

export const activitiesAPI = {
  list(params = {}) {
    return apiClient.get(`/${STANDARD}/activities`, { params });
  },

  get(id) {
    return apiClient.get(`/${STANDARD}/activities/${id}`);
  },
};

export const msCourseAPI = {
  listCourses(params = {}) {
    return apiClient.get(`/${STANDARD}/courses`, { params });
  },

  getCourseDetail(courseId) {
    return apiClient.get(`/${STANDARD}/courses/${courseId}`);
  },

  listCategories() {
    return apiClient.get(`/${STANDARD}/course-categories`);
  },

  listTags() {
    return apiClient.get(`/${STANDARD}/course-tags`);
  },

  addFavorite(courseId) {
    return apiClient.post(`/${STANDARD}/courses/${courseId}/favorite`);
  },

  removeFavorite(courseId) {
    return apiClient.delete(`/${STANDARD}/courses/${courseId}/favorite`);
  },

  listFavorites(params = {}) {
    return apiClient.get(`/${STANDARD}/me/favorites/courses`, { params });
  },
};

export const msSupportAPI = {
  submitInquiry(payload) {
    return apiClient.post(`/${STANDARD}/support`, payload);
  },
};

export const certificatesAPI = {
  list() {
    return apiClient.get(`/${STANDARD}/member/student-certificates`);
  },

  download(id) {
    return apiClient.get(`/${STANDARD}/member/student-certificates/${id}/download`, {
      responseType: 'blob',
    });
  },
};

export const authStorage = {
  getToken: getAuthToken,
  setToken: setAuthToken,
  clearToken: clearAuthToken,
  tokenKey: TOKEN_STORAGE_KEY,
};
